/**
 * Web Icons Registry
 *
 * Provides web technology icons from devicons-react package.
 * Includes frontend frameworks, backend frameworks, web servers,
 * databases, and programming languages.
 *
 * Note: devicons-react uses `size` prop (not `width`/`height`).
 *
 * @see https://www.npmjs.com/package/devicons-react
 */

import {
  AngularOriginal,
  ApacheOriginal,
  DjangoPlain,
  ExpressOriginal,
  FlaskOriginal,
  JavascriptOriginal,
  MongodbOriginal,
  NextjsOriginal,
  NginxOriginal,
  NodejsOriginal,
  PostgresqlOriginal,
  ReactOriginal,
  RedisOriginal,
  TypescriptOriginal,
  VuejsOriginal,
} from 'devicons-react';
import type { ComponentType, ReactNode } from 'react';

import { useTheme } from '@/app/ThemeContext';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { WEB_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type WebIconComponent = ComponentType<{
  className?: string;
  size?: number;
  color?: string;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Theme-Aware Icon Wrappers
// ============================================================================

/**
 * Wrapper for icons that need CSS invert in dark mode.
 * Uses useTheme hook to react to theme changes.
 */
const createInvertIcon = (
  IconComponent: WebIconComponent
): WebIconComponent => {
  return function InvertedIcon({
    size,
    className,
  }: {
    size?: number;
    className?: string;
  }) {
    const { currentTheme } = useTheme();
    const isDark = currentTheme === 'dark';

    return (
      <span
        className={`inline-flex ${className ?? ''}`}
        style={{ filter: isDark ? 'invert(1)' : 'none' }}
      >
        <IconComponent size={size} />
      </span>
    );
  };
};

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from WEB_ICONS names to devicons-react components.
 * Uses type-safe keys derived from WEB_ICONS const array.
 * Icons that need dark mode handling are wrapped with appropriate handlers.
 */
const webIconComponents: Partial<
  Record<(typeof WEB_ICONS)[number], WebIconComponent>
> = {
  // Frontend Frameworks
  React: ReactOriginal,
  Vue: VuejsOriginal,
  Angular: AngularOriginal,
  Nextjs: createInvertIcon(NextjsOriginal),

  // Backend Frameworks
  Nodejs: NodejsOriginal,
  Express: createInvertIcon(ExpressOriginal),
  Django: DjangoPlain,
  Flask: createInvertIcon(FlaskOriginal),

  // Web Servers
  Nginx: NginxOriginal,
  Apache: ApacheOriginal,

  // Databases
  Mongodb: MongodbOriginal,
  Postgresql: PostgresqlOriginal,
  Redis: RedisOriginal,

  // Languages
  Javascript: JavascriptOriginal,
  Typescript: TypescriptOriginal,
};

/**
 * Human-readable labels for web icons.
 */
const webIconLabels: Partial<Record<(typeof WEB_ICONS)[number], string>> = {
  React: 'React',
  Vue: 'Vue.js',
  Angular: 'Angular',
  Nextjs: 'Next.js',
  Nodejs: 'Node.js',
  Express: 'Express',
  Django: 'Django',
  Flask: 'Flask',
  Nginx: 'Nginx',
  Apache: 'Apache',
  Mongodb: 'MongoDB',
  Postgresql: 'PostgreSQL',
  Redis: 'Redis',
  Javascript: 'JavaScript',
  Typescript: 'TypeScript',
};

// ============================================================================
// Web Renderer Implementation
// ============================================================================

/**
 * Web icon renderer for the attack graph icon system.
 * Implements IconRenderer interface for registration with iconRenderers Map.
 */
const webRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = webIconComponents[name as (typeof WEB_ICONS)[number]];
    if (!IconComponent) return null;
    // devicons-react uses `size` prop instead of width/height
    return <IconComponent className={className} size={size} />;
  },

  validate: (name: string): boolean => {
    return (WEB_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = webIconComponents[name as (typeof WEB_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedWebIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} size={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    WEB_ICONS.map(name => {
      const IconComponent = webIconComponents[name];
      return {
        id: `web:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: webIconLabels[name] ?? name,
        category: 'web',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('web', webRenderer);

export { webRenderer };
